import React from 'react';

import {Alert, Button, Card, CardContent, CardFooter, CardHeader} from '@design-system';

import useRetryFocus from '../Util/useRetryFocus';
import useRetryHold from '../Util/useRetryHold';

/**
 * The loading, empty and failure states both Professional resource families
 * share, at index, category and detail depth.
 */

/** The single-to-three column card grid used by every resource list. */
export const RESOURCE_GRID_CLASSES =
  'm-0 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 xl:grid-cols-3';

// Pulses only when the person has not asked for reduced motion.
const PLACEHOLDER_CLASSES = 'rounded-md bg-(--compass-surface-muted) motion-safe:animate-pulse';

/** A decorative placeholder with the shape of a resource card. */
const LoadingCard = () => {
  return (
    <Card aria-hidden="true" data-testid="pro-loading-card" className="flex h-full flex-col">
      <CardHeader className="flex-row items-center gap-3">
        <span
          data-slot="placeholder"
          className={`size-10 shrink-0 rounded-full ${PLACEHOLDER_CLASSES}`}
        />
        <span data-slot="placeholder" className={`h-4 flex-1 ${PLACEHOLDER_CLASSES}`} />
      </CardHeader>
      <CardContent>
        <span data-slot="placeholder" className={`block h-3 w-3/4 ${PLACEHOLDER_CLASSES}`} />
      </CardContent>
      <CardFooter>
        <span data-slot="placeholder" className={`block h-11 w-32 ${PLACEHOLDER_CLASSES}`} />
      </CardFooter>
    </Card>
  );
};

/**
 * An announced outcome that is not a failure: an empty list or a missing record.
 * It appears once a load has answered, so it stays a polite status, the info
 * Alert's own live role.
 */
export const ResourceNotice = ({children}) => (
  <Alert variant="info" data-testid="pro-resource-notice">
    {children}
  </Alert>
);

/**
 * Renders `children` once a resource query has data, and an announced loading
 * status or an in-place alert with Retry until then.
 *
 * It stays mounted across every state, which is what lets it own the Retry:
 * `useRetryHold` keeps the alert, and the Retry that has focus, on screen until
 * the retry has its answer, with no loading state between.
 *
 * Focus moves to the alert when a failure first shows, so it is read out with
 * Retry the next stop. A Retry keeps focus through its request and through a
 * failure that follows it, because the alert and its Retry stay mounted.
 */
export const ResourceQueryState = ({
  data,
  error,
  refetch,
  label,
  placeholderCount = 0,
  children,
}) => {
  const {retrying, retryPending, retry} = useRetryHold({data, error, refetch});

  const showFailure = data === undefined && Boolean(error || retrying);
  // Retry is not pressed through useRetryFocus's own `retry`: the failure stays
  // on screen throughout, so only its first appearance moves focus.
  const {failureRef} = useRetryFocus({failed: showFailure}, retry);

  if (showFailure) {
    return (
      <Alert
        ref={failureRef}
        variant="error"
        data-testid="pro-resource-error"
        action={
          <Button
            type="button"
            variant="outline"
            data-testid="pro-resource-retry"
            className="min-h-11"
            aria-busy={retryPending ? true : undefined}
            onClick={retry}>
            Retry
          </Button>
        }>
        {`We couldn't load the ${label}. Please try again.`}
      </Alert>
    );
  }

  if (data === undefined) {
    return (
      <div data-testid="pro-resource-loading">
        <p role="status" className="text-sm text-(--compass-on-surface)">
          {`Loading ${label}…`}
        </p>
        {placeholderCount > 0 ? (
          <div aria-hidden="true" className={`mt-4 ${RESOURCE_GRID_CLASSES}`}>
            {Array.from({length: placeholderCount}, (_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return children;
};

export default LoadingCard;
